import { dateLocale } from "@/lib/dateTime";
import { Mail, Pencil, Phone, Pin, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VisitActions } from "@/components/visits/VisitActions";
import { StatusPill, initialsOf } from "@/components/patterns";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { usePins } from "@/lib/usePins";
import { useToast } from "@/context/ToastContext";
import { formatINRCompact } from "@/lib/utils";
import type { Account360, DealRecord } from "@/types";

const dayShort = (d?: string | null) => (d ? new Date(d).toLocaleDateString(dateLocale(), { day: "numeric", month: "short" }) : "");

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "success" | "primary" | "warning" }) {
  return (
    <div className="flex flex-col rounded-lg border border-border px-3 py-2">
      <span className="text-[11.5px] text-muted-foreground">{label}</span>
      <span className="font-mono text-base font-semibold">{value}</span>
      <span className={tone === "success" ? "text-[11.5px] text-success" : tone === "primary" ? "text-[11.5px] text-primary" : tone === "warning" ? "text-[11.5px] text-warning" : "text-[11.5px] text-muted-foreground"}>{sub}</span>
    </div>
  );
}

/** Who the account is, what can be done now, and five numbers that say how the relationship is going */
export function AccountRecordHeader({
  data,
  can,
  onCall,
  onWhatsApp,
  onEmail,
  onEdit,
  onNewDeal,
}: {
  data: Account360;
  can: { update: boolean; whatsapp: boolean; deal: boolean };
  onCall: () => void;
  onWhatsApp: () => void;
  onEmail: () => void;
  onEdit: () => void;
  onNewDeal: () => void;
}) {
  const { account, deals } = data;
  const roll = data.rollup;
  const pins = usePins();
  const { error } = useToast();
  const { pathname } = window.location;
  const owner = account.owner ? `${account.owner.first_name} ${account.owner.last_name || ""}`.trim() : "";

  const won = deals.filter((d: DealRecord) => d.deal_status === "WON");
  const lost = deals.filter((d: DealRecord) => d.deal_status === "LOST");
  const closedCount = won.length + lost.length;
  const winRate = closedCount ? Math.round((won.length / closedCount) * 100) : null;
  const lastWon = [...won].filter((d) => d.actual_close_date).sort((a, b) => new Date(b.actual_close_date!).getTime() - new Date(a.actual_close_date!).getTime())[0];
  const firstWon = [...won].filter((d) => d.actual_close_date).sort((a, b) => new Date(a.actual_close_date!).getTime() - new Date(b.actual_close_date!).getTime())[0];
  const touched = roll?.last_activity_on ? new Date(roll.last_activity_on) : null;
  const touchDays = touched ? Math.floor((Date.now() - touched.getTime()) / 86400000) : null;
  const sub = [account.industry, account.state].filter(Boolean);

  return (
    <section aria-label="Account summary" className="flex flex-col gap-3.5 rounded-lg border border-border bg-card px-4 py-3.5">
      <div className="flex flex-wrap items-start gap-3.5">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-sidebar text-sm font-bold text-white">{initialsOf(account.name)}</span>
        <div className="flex min-w-[240px] flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[19px] font-semibold leading-tight tracking-tight">{account.name}</h1>
            {firstWon ? <StatusPill tone="success">Customer since {new Date(firstWon.actual_close_date!).getFullYear()}</StatusPill> : <StatusPill tone="primary">Prospect</StatusPill>}
            <Button
              variant="outline"
              size="icon-sm"
              aria-pressed={pins.isPinned(pathname)}
              aria-label={pins.isPinned(pathname) ? "Unpin from Home" : "Pin to Home"}
              title={pins.isPinned(pathname) ? "Unpin from Home" : "Pin to Home"}
              onClick={() => pins.toggle(pathname, account.name).catch((e) => error("Could not change the pin", e?.message || "Try again"))}
            >
              <Pin className={`size-3.5 ${pins.isPinned(pathname) ? "fill-current text-gold" : ""}`} />
            </Button>
          </div>
          <div className="text-[13px] text-muted-foreground">
            {sub.join(" · ")}
            {account.gstin && (
              <>
                {sub.length > 0 && " · "}
                <span className="font-mono text-xs">GSTIN {account.gstin}</span>
              </>
            )}
            {owner && (
              <>
                {" · Owner "}
                <strong className="font-medium text-foreground">{owner}</strong>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {account.phone && (
            <Button variant="outline" size="sm" onClick={onCall}>
              <Phone className="mr-1.5 size-3.5" />
              Call
            </Button>
          )}
          {account.phone && can.whatsapp && (
            <Button variant="outline" size="sm" onClick={onWhatsApp}>
              <WhatsAppIcon className="mr-1.5 size-3.5" />
              WhatsApp
            </Button>
          )}
          {account.email && (
            <Button variant="outline" size="sm" onClick={onEmail}>
              <Mail className="mr-1.5 size-3.5" />
              Email
            </Button>
          )}
          <VisitActions customer={{ type: "ACCOUNT", id: account.id, label: account.name }} />
          {can.update && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="mr-1.5 size-3.5" />
              Edit
            </Button>
          )}
          {can.deal && (
            <Button size="sm" className="bg-sidebar text-white hover:bg-sidebar/90" onClick={onNewDeal}>
              <Plus className="mr-1 size-3.5" />
              New deal
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 @2xl/main:grid-cols-3 @6xl/main:grid-cols-5">
        <Kpi label="Open pipeline" value={roll?.open_value ? formatINRCompact(roll.open_value) : "—"} sub={roll?.open_deals ? `${roll.open_deals} deal${roll.open_deals === 1 ? "" : "s"}` : "No open deals"} />
        <Kpi label="Won this year" value={roll?.won_year ? formatINRCompact(roll.won_year) : "—"} sub={won.length ? `${won.length} won in all` : "Nothing won yet"} tone={roll?.won_year ? "success" : undefined} />
        <Kpi label="Win rate" value={winRate === null ? "—" : `${winRate}%`} sub={closedCount ? `${won.length} of ${closedCount} deals` : "No closed deals"} />
        <Kpi label="Last order" value={lastWon ? dayShort(lastWon.actual_close_date) : "—"} sub={lastWon ? `${lastWon.name.slice(0, 22)} · ${formatINRCompact(lastWon.amount)}` : "No orders yet"} />
        <Kpi
          label="Last contact"
          value={touchDays === null ? "—" : touchDays <= 0 ? "Today" : touchDays === 1 ? "Yesterday" : dayShort(roll!.last_activity_on)}
          sub={roll?.last_activity_what ? roll.last_activity_what.slice(0, 28) : "Nothing logged"}
          tone={touchDays !== null && touchDays > 30 ? "warning" : touchDays !== null && touchDays <= 1 ? "primary" : undefined}
        />
      </div>
    </section>
  );
}
